import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * "From Strategy to Success: Mastering the Art of Digital Transformation" — the
 * e-book's own page.
 *
 * Built to the design of the digitopia.co page it replaces
 * (`/impact-labs/ebooks/mastering-the-art-of-digital-transformation/`), sent as
 * four screenshots on 2026-08-28 with *"referans alarak ilgili sayfayı tastamam
 * şekilde, aynısını, tıpkısını tasarla"*. Band for band that page's: the
 * two-column hero with the blue light-trail artwork beside it, the grey "Why
 * Download?" card with the pair of cover mockups, the "What You'll Learn" card
 * with the stacked copies and the five lessons, and the centred "Access the
 * Guide to Successful Transformation" ask. The copy is that page's copy,
 * verbatim.
 *
 * Seventh e-book page after Transform Better, Agentic AI, Sustainability
 * Innovation, Measurement, AI in Fashion Retail and the Generative AI guide, and
 * it follows them wherever the reference and this site disagree — the same three
 * deliberate departures, for the same reasons:
 *
 *  * **The eyebrow is the site's own**, over the copy column, rather than
 *    digitopia.co's small left-pinned "FROM STRATEGY TO SUCCESS" label. Every
 *    other hero here is that shape, and the words are unchanged.
 *  * **The download is a direct link to the PDF, not a gated form.** The
 *    reference puts a nine-field HubSpot form between the reader and the file;
 *    no form on this site submits anywhere yet, so reproducing it would mean the
 *    page's only call to action silently drops every lead. The `check` below
 *    says so, as the sibling pages' do.
 *  * **A testimonial closes the page**, which the reference does not have — it is
 *    what the sibling e-book pages do, asked for on 2026-08-28 (*"en aşağıda
 *    önceden konuştuğumuz gibi testimonial template ini kullanalım"*). The card
 *    is Lila Kağıt's: of the twenty in `content/testimonials.ts` it is the one
 *    about **mobilising departments towards common goals** and "strategic
 *    alignment", which is this book's own subject — align teams, then execute.
 *    Its words are the Hall of Fame's; that file is where a change to them
 *    belongs.
 *
 * **The slug is `mastering-the-art-of-digital-transformation`**, which is
 * digitopia.co's own — the reference screenshots carry the address in their
 * filenames — and **not** the `from-strategy-to-success` the catalogue derived
 * from the part of the title before the colon. Fifth time the title rule has
 * been caught out; `content/ebooks.ts` is corrected to match.
 *
 * **The three "What You'll Learn" lessons are flattened by one level.** The
 * reference sets each of them as a bolded lesson over an explaining line, and a
 * card's `points` is a flat list of strings — so each point here is the lesson,
 * then what it means, in that order and with nothing lost.
 */

/**
 * The PDF, in the `website-documents` bucket rather than in `public/`.
 *
 * The same object `content/ebooks.ts` already lists for this e-book's card,
 * spelled out here because the page needs it on four buttons and that file's
 * `pdf()` helper is private to the catalogue. It is a local constant rather than
 * a key in `content/pages/links.ts` because a page's own PDF is nobody else's
 * business — and `links.ts` is a shared file that concurrent editor runs collide
 * on.
 */
const PDF
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
    + '300ca4f7-0413-410e-9fce-24e4164020b6-Mastering%20the%20Art%20of%20Digital%20Transformation.pdf'

export const route: RouteDef = {
  id: 'ebookMasteringDigitalTransformation',
  path: 'impact-labs/ebooks/mastering-the-art-of-digital-transformation',
  priority: 0.7,
}

export const ebookMasteringDigitalTransformation: WrittenPage = {
  navLabel: 'Mastering the Art of Digital Transformation',
  title: 'Mastering the Art of Digital Transformation',
  description:
    'Navigate complexity. Align teams. Deliver real transformation. Download the practical '
    + 'playbook for moving from strategy to execution, from Digitopia Impact Labs.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'From Strategy to Success',
      headline: 'Mastering the Art of Digital Transformation',
      sub: 'Navigate complexity. Align teams. Deliver real transformation.',
      body: [
        'Digital transformation is essential, but many organisations struggle to turn strategy '
          + 'into results. This ebook outlines what truly drives successful execution, helping '
          + 'you move from planning to real progress.',
      ],
      ctas: [{ label: 'Download', href: PDF }],
      photo: {
        src: '/photo/mastering-digital-transformation-ebook-artwork.webp',
        alt:
          'The e-book’s cover artwork: long-exposure blue and white light trails curving away '
          + 'from a dark centre.',
      },
      // The artwork is square and the site's hero box is 3:2, which would take
      // the top and bottom off the light trails.
      photoFrame: 'square',
      /*
        Clause 1 — THE DOWNLOAD IS NOT GATED — resolved 2026-08-30. It said no
        form on this site submits anywhere and the Send button is disabled on
        purpose. Both stopped being true today: the form posts to
        `website-form-lead`, the submission opens a task on its page's own
        counterpart, and every Download here carries `data-gated-download`, so
        the reader is asked for an email and the PDF is sent to it. No HubSpot
        form id is needed for any of it. What is left is clause 2.
      */
      check:
        'THE ARTWORK IS 768–900px WIDE. Those are the largest copies of the '
        + 'cover artwork and the two mockups anybody has sent; every other hero on '
        + 'the site is 2560px, so this one is painted at roughly its own width on a '
        + 'laptop with nothing left over for a retina screen. If bigger exports '
        + 'exist wherever the covers were made:\n'
        + '     node apps/website/scripts/attach.mjs <file> photo-src/mastering-digital-transformation-ebook-artwork.webp\n'
        + '     pnpm --filter @digitopia/website images\n'
        + '   and nothing in this file changes.',
    },
    {
      kind: 'cta',
      tone: 'card',
      // Mockup left, offer right — the reference's order, and it alternates with
      // the card under it.
      flip: true,
      heading: 'Why Download?',
      sub:
        'This ebook is a practical guide for transformation leaders and digital executives to '
        + 'bridge the gap between vision and execution.',
      points: [
        'Understand why most transformations fail, and how to avoid it',
        'Align people, processes, and technologies around a shared goal',
        'Learn from real examples of success',
        'Lead change with clarity in today’s fast-paced landscape',
      ],
      ctas: [{ label: 'Download the Guidebook', href: PDF }],
      photo: {
        src: '/photo/mastering-digital-transformation-ebook-covers.webp',
        alt:
          'Two copies of Mastering the Art of Digital Transformation, one behind the other, on a '
          + 'white background.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'What You’ll Learn',
      points: [
        'The Key Ingredients of a Successful Transformation: understand the core success factors '
          + 'that set leading organisations apart.',
        'How to Build an Aligned Transformation Strategy: practical ways to align teams and '
          + 'leadership around a clear, shared direction.',
        'Overcoming the Execution Gap: how to move from plans to action, and keep the momentum '
          + 'going.',
        'Best Practices for Change Management: tackle resistance and navigate complexity with '
          + 'confidence.',
        'Benchmarks and Real-World Insights: explore how other companies measure up and learn '
          + 'from their journeys.',
      ],
      ctas: [{ label: 'Download the Guidebook', href: PDF }],
      photo: {
        src: '/photo/mastering-digital-transformation-ebook-stack.webp',
        alt:
          'A stack of printed copies of Mastering the Art of Digital Transformation seen from '
          + 'above.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Access the Guide to Successful Transformation',
      sub: 'Download the ebook and take the next step in delivering transformation that matters.',
      cta: { label: 'Download the Guidebook', href: PDF },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'What our customers say.',
      items: [
        {
          // The Hall of Fame's Lila Kağıt card, word for word —
          // `content/testimonials.ts` is where these words are edited, and the
          // Turkish page carries its own saying of them.
          quote:
            'The digital maturity assessment has been a crucial tool in mobilizing our '
            + 'departments towards common goals. Thanks to Digitopia’s Digital Maturity Index, '
            + 'we gained a clear understanding of our position in key areas such as customer, '
            + 'governance, operations, people, innovation, and technology. With the guidance of '
            + 'a moderator and the competitive insights provided, our teams were able to '
            + 'position themselves within the market and take ownership of their transformation '
            + 'steps. We ensured accountability across departments and achieved strategic '
            + 'alignment.',
          source: 'Tarkan Ateşoğlu, Information Technology Group Manager',
          company: 'Lila Kağıt',
          logo: { name: 'Lila Kağıt', src: '/logo/lilakagitcard.png' },
          portrait: { src: '/photo/portrait-tarkan-atesoglu.png', alt: 'Tarkan Ateşoğlu' },
        },
      ],
    },
  ],
}
